import Header from "../../Components/Header";
import PortalContext from "../PortalContext";
import { useGetTranslation } from "../../Components/Translate";
import ProgressBar from "../../Components/ProgressBar";
import { useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  sendPostRequest,
  useGetRequest,
  sendGetRequest,
} from "../../Client/index.js";
import ClipLoader from "react-spinners/ClipLoader";
import DOMPurify from "dompurify";
import DropoffModal from "./DropoffModal";

function Linkify({ text }) {
  const urlRegex = /(https?:\/\/[^\s<>"']*[^\s<>"'.,!?;:])/g;

  return (
    <>
      {text.split(urlRegex).map((part, index) =>
        /^https?:\/\//.test(part) ? (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#0d6efd", textDecoration: "underline" }}
          >
            {part}
          </a>
        ) : (
          part
        )
      )}
    </>
  );
}

export default function Step5() {
  const { portal } = useContext(PortalContext);
  const getTranslation = useGetTranslation();

  const [labelError, setLabelError] = useState("");
  const [formError, setFormError] = useState("");
  const [labelLoading, setLabelLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [dropoffLoading, setDropoffLoading] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [showDropoffs, setShowDropoffs] = useState(false);
  const [dropoffs, setDropoffs] = useState({
    loading: false,
    logoURL: "",
    serviceNameCustomer: "",
    items: [],
    error: "",
  });

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const id = params.get("s");

  const { status, data } = useGetRequest(
    `api/v2-label-portal-order-details.php?id=${id}`,
    {}
  );

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (!showDropoffs) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [showDropoffs]);

  const downloadLabel = async () => {
    setLabelError("");
    setFormError("");
    setLabelLoading(true);

    const res = await sendPostRequest(`api/v2-label-portal-label.php`, { id });

    if (res.status === "error") {
      setLabelError(res.reason || "Error generating label");
      setLabelLoading(false);
      return;
    }

    fetchPdf(res.pdf, "Label.pdf");
    setLabelLoading(false);
  };

  const downloadForm = async () => {
    setLabelError("");
    setFormError("");
    setFormLoading(true);
    const res = await sendPostRequest(`api/v2-label-portal-form.php`, { id });
    fetchPdf(res.pdf, "Returns_Form.pdf");
    setFormLoading(false);
  };

  const fetchPdf = async (pdf, filename) => {
    const base64String = pdf;
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length)
      .fill()
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const dropoffLocations = async (data) => {
    setDropoffLoading(true);

    try {
      const res = await sendGetRequest(
        `api/v2-label-portal-services-dropoff-points.php?serviceProvider=${data.data.serviceProvider
        }&country=${data.data.countryISO}&postcode=${encodeURIComponent(
          data.data.postcode || ""
        )}&address1=${encodeURIComponent(
          data.data.address1 || ""
        )}&town=${encodeURIComponent(
          data.data.town || ""
        )}&county=${encodeURIComponent(
          data.data.county || ""
        )}&serviceExternalCode=${encodeURIComponent(
          data.data.serviceExternalCode || ""
        )}&serviceID=${encodeURIComponent(data.data.serviceID || "")}`
      );

      setDropoffs({
        loading: false,
        logoURL: res?.logoURL || "",
        serviceNameCustomer: res?.serviceNameCustomer || "",
        content: res?.content || "",
        items: Array.isArray(res?.locations) ? res.locations : [],
        error:
          Array.isArray(res?.locations) && res.locations.length
            ? ""
            : "No drop-off points found nearby.",
      });
    } catch {
      setDropoffs({
        loading: false,
        items: [],
        error: "Failed to load drop-off locations.",
      });
    }

    setShowDropoffs(true);
    setDropoffLoading(false);
  };

  const safeHtml = (html = "") => ({ __html: DOMPurify.sanitize(html) });

  return (
    <>
      <Header />

      <section className="page3-slider py-lg-3">
        <div className="container">
          <div className="row d-flex align-items-center mt-3">
            {portal?.step1 && (
              <div className="col-12 mb-3">
                <ProgressBar step={4} />
              </div>
            )}

            {status === "fetching" ? (
              <div className="col-12">
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{
                    minHeight: "50vh",
                    width: "100%",
                  }}
                >
                  <ClipLoader
                    color={
                      portal?.data?.settings?.colours?.primary || "#d9e5f4"
                    }
                    size={80}
                    aria-label="Loading"
                    role="status"
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="col-12 col-lg-8 pb-5">
                  <h2>
                    {getTranslation(
                      "STEP5_TITLE",
                      "Your order has been processed"
                    )}
                  </h2>

                  {data?.customer && (
                    <div
                      className="mb-1 flex items-start"
                      style={{
                        backgroundColor: "#fff4df",
                        borderRadius: 8,
                        padding: 5,
                        border: "1px solid #7e322e",
                        color: "#7e322e",
                        display: "flex",
                        alignItems: "center",
                        marginTop: 20,
                      }}
                    >
                      <div
                        className="hide-on-mobile"
                        style={{
                          width: "15%",
                          fontSize: "5rem",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        !
                      </div>

                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: "1rem",
                          lineHeight: "1.5rem",
                          width: "100%",
                          padding: "2%",
                        }}
                      >
                        <div
                          className="pt-3 text-muted mb-1"
                          dangerouslySetInnerHTML={safeHtml(data.customer)}
                        />
                      </div>
                    </div>
                  )}

                  {data?.service && (
                    <div
                      className="pt-3 text-muted mb-1"
                      dangerouslySetInnerHTML={safeHtml(data.service)}
                    />
                  )}

                  {data?.general && (
                    <div
                      className="pt-3 text-muted mb-1"
                      dangerouslySetInnerHTML={safeHtml(data.general)}
                    />
                  )}

                  <div className="row mt-4">
                    <div className="col-sm-12 col-md-3">
                      <button
                        type="submit"
                        className="btn-block"
                        style={{
                          backgroundColor: portal.data.settings.colours.primary,
                          border: "none",
                        }}
                        onClick={downloadLabel}
                      >
                        {labelLoading ? (
                          <ClipLoader size={16} color="white" />
                        ) : (
                          getTranslation("STEP5_LABEL", "Download label")
                        )}
                      </button>
                    </div>

                    <div className="col-sm-12 col-md-3">
                      <button
                        type="button"
                        onClick={downloadForm}
                        className="btn-block"
                        style={{
                          backgroundColor: "white",
                          borderColor: portal.data.settings.colours.primary,
                          borderWidth: 1,
                          borderStyle: "solid",
                          color: portal.data.settings.colours.primary,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 6,
                          padding: "0 16px",
                          lineHeight: 1,
                          fontWeight: 600,
                        }}
                      >
                        <span
                          style={{
                            color: portal.data.settings.colours.primary,
                            fontWeight: 500,
                          }}
                        >
                          {formLoading ? (
                            <ClipLoader
                              size={16}
                              color={portal.data.settings.colours.primary}
                            />
                          ) : (
                            getTranslation("STEP5_FORM", "Download form")
                          )}
                        </span>
                      </button>
                    </div>

                    <div className="col-sm-12 col-md-3">
                      <button
                        type="submit"
                        className="btn-block"
                        style={{
                          backgroundColor: "white",
                          border: "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          height: 42,
                        }}
                        onClick={() => dropoffLocations(data)}
                        disabled={dropoffLoading}
                      >
                        {dropoffLoading ? (
                          <ClipLoader
                            size={18}
                            color={
                              portal?.data?.settings?.colours?.primary ||
                              "#e39408"
                            }
                          />
                        ) : (
                          <span
                            style={{
                              color: portal.data.settings.colours.primary,
                              fontWeight: 500,
                            }}
                          >
                            {getTranslation(
                              "STEP5_DROPOFF",
                              "Dropoff locations"
                            )}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>

                  {labelError && (
                    <div
                      style={{
                        backgroundColor: "#f8d7da",
                        color: "#842029",
                        fontWeight: 500,
                        marginTop: "1rem",
                        padding: "0.75rem 1rem",
                        borderRadius: 8,
                        border: "1px solid #f5c2c7",
                        maxWidth: 640,
                        textAlign: "center",
                      }}
                      role="alert"
                    >
                      {labelError}
                    </div>
                  )}

                  {formError && (
                    <div
                      style={{
                        backgroundColor: "#f8d7da",
                        color: "#842029",
                        fontWeight: 500,
                        marginTop: "1rem",
                        padding: "0.75rem 1rem",
                        borderRadius: 8,
                        border: "1px solid #f5c2c7",
                        maxWidth: 640,
                        textAlign: "center",
                      }}
                      role="alert"
                    >
                      {formError}
                    </div>
                  )}
                </div>

                <div className="col-12 col-lg-4 text-center ">
                  {portal?.data?.settings?.imageUrl && (
                    <img
                      src={portal?.data?.settings?.imageUrl}
                      className="w-100 img-fluid"
                      alt="Retailer Image"
                    />
                  )}

                  <div className="d-flex justify-content-end">
                    <img
                      src={require("../../Assets/Images/powered-by.png")}
                      className="img-fluid powered-home-logo mt-2 mb-2"
                      alt="Powered by"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {showDropoffs && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Dropoff locations"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowDropoffs(false);
            }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              padding: 20,
            }}
          >
            {dropoffs.items.length > 0 ? (
              <DropoffModal
                dropoffs={dropoffs}
                portal={portal}
                onClose={() => setShowDropoffs(false)}
              />
            ) : (
              <div
                style={{
                  width: isMobile ? "min(420px, 92vw)" : "40%",
                  background: "#fff",
                  borderRadius: 12,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header */}
                <div
                  style={{
                    padding: "14px 18px",
                    borderBottom: "1px solid #eee",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexShrink: 0,
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {dropoffs.logoURL && (
                      <img
                        src={dropoffs.logoURL}
                        alt="Service Logo"
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "contain",
                          borderRadius: 8,
                          background: "#fafafa",
                          flexShrink: 0,
                        }}
                      />
                    )}

                    <div style={{ flex: 1 }}>
                      <h5
                        style={{
                          margin: 0,
                          fontSize: 17,
                          fontWeight: 600,
                          whiteSpace: "normal",
                          overflow: "visible",
                          textOverflow: "unset",
                          wordBreak: "break-word",
                        }}
                      >
                        {dropoffs.serviceNameCustomer}
                      </h5>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowDropoffs(false)}
                    style={{
                      border: "none",
                      background: "transparent",
                      fontSize: 26,
                      lineHeight: "1",
                      cursor: "pointer",
                      color: "#333",
                      fontWeight: 600,
                      padding: "0 4px",
                      flexShrink: 0,
                    }}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>

                {/* Body */}
                <div
                  style={{
                    padding: "24px 28px",
                    textAlign: "center",
                  }}
                >

                  {dropoffs.content ? (
                    <p
                      style={{
                        fontSize: 14,
                        color: "#666",
                        lineHeight: 1.5,
                        marginBottom: 20,
                      }}
                    >
                      <Linkify text={dropoffs.content} />
                    </p>
                  ) : (
                    <>
                      <h4
                        style={{
                          marginBottom: 10,
                          fontSize: 18,
                          fontWeight: 600,
                          color: "#222",
                        }}
                      >
                        No drop-off locations found
                      </h4>
                      <p
                        style={{
                          fontSize: 14,
                          color: "#666",
                          lineHeight: 1.5,
                          marginBottom: 20,
                        }}
                      >
                        We couldn’t find any drop-off points for the service you
                        selected.
                      </p>
                    </>
                  )}
                  <button
                    onClick={() => setShowDropoffs(false)}
                    style={{
                      background:
                        portal?.data?.settings?.colours?.primary || "#e39408",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      padding: "10px 20px",
                      fontSize: 14,
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </>
  );
}
